import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Linkedin, Youtube, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/companyInfo';
import { ROUTE_MAP, NAV_LABELS } from '../data/routes';
import WhatsAppIcon from './WhatsAppIcon';

const Footer = () => {
  const { locale, t } = useLanguage();
  const location = useLocation();
  const routes = ROUTE_MAP[locale];
  const labels = NAV_LABELS[locale] || {};
  const company = companyInfo[locale] || {};
  const primaryPhone = '+90 216 401 10 50';
  const secondaryPhone = '+90 539 659 10 50';
  const whatsappHref = 'https://api.whatsapp.com/send?phone=905396591050';

  const footerText = {
    aboutTitle: t('Demart Mühendislik', 'Demart Engineering', 'Demart Mühəndislik'),
    aboutDesc: company?.subtitle || t(
      'Sofis Türkiye & Azerbaycan Resmi Distribütörü',
      'Official Sofis Distributor in Turkiye & Azerbaijan',
      'Sofis-in Türkiyə & Azərbaycan rəsmi distribütoru'
    ),
    quickLinks: t('Hızlı Linkler', 'Quick Links', 'Sürətli Keçidlər'),
    productsTitle: t('Ürünler', 'Products', 'Məhsullar'),
    contactTitle: t('İletişim', 'Contact', 'Əlaqə'),
    copyright: t(
      '© 2024 Demart Mühendislik. Tüm hakları saklıdır.',
      '© 2024 Demart Engineering. All rights reserved.',
      '© 2024 Demart Mühəndislik. Bütün hüquqlar qorunur.'
    ),
    privacyPolicy: t('Gizlilik Politikası', 'Privacy Policy', 'Məxfilik Siyasəti'),
    cookiePolicy: t('Çerez Politikası', 'Cookie Policy', 'Çərəz Siyasəti')
  };

  const quickLinks = [
    { name: t('Ana Sayfa', 'Home', 'Ana Səhifə'), href: routes.home },
    { name: labels.valveMaintenance, href: routes.valveMaintenance },
    { name: labels.products, href: routes.products },
    { name: labels.services, href: routes.services },
    { name: labels.solutions, href: routes.solutions },
    { name: labels.applications, href: routes.applications },
    { name: labels.industries, href: routes.industries },
    { name: labels.customerStories, href: routes.customerStories },
    { name: labels.energyTransition, href: routes.energyTransition },
    { name: labels.about, href: routes.about },
    { name: labels.faq, href: routes.faq },
    { name: labels.contact, href: routes.contact },
  ];

  const visibleQuickLinks = quickLinks.filter(link => link.name);
  const quickLinksMidpoint = Math.ceil(visibleQuickLinks.length / 2);
  const quickLinksLeftColumn = visibleQuickLinks.slice(0, quickLinksMidpoint);
  const quickLinksRightColumn = visibleQuickLinks.slice(quickLinksMidpoint);

  const productLinks = [
    { name: t('Vana Kilitleri', 'Valve Interlocks', 'Vana Kilidləri'), href: `${routes.products}/netherlocks-interlocks` },
    { name: t('Taşınabilir Aktüatörler', 'Portable Actuators', 'Daşınan Aktuatorlar'), href: `${routes.products}/easidrive` },
    { name: t('Uzaktan Vana Operasyonu', 'Remote Valve Operation', 'Uzaqdan Vana Əməliyyatı'), href: `${routes.products}/flexidrive` },
    { name: t('Vana Pozisyon Göstergesi', 'Valve Position Indicator', 'Vana Pozisiya Göstəricisi'), href: `${routes.products}/vpi` },
    { name: t('Tüm Ürünler', 'All Products', 'Bütün Məhsullar'), href: routes.products },
  ];

  const ctaByRoute = {
    [routes.home]: {
      title: t('Demart ile iş yapmak çok kolay!', 'Working with Demart is easy!', 'Demart ilə işləmək çox asandır!'),
      desc: t(
        'Vana operasyonlarınızı optimize etmek için bilgi ve uzmanlığımızdan faydalanın.',
        'Leverage our know-how to optimize your valve operations.',
        'Vana əməliyyatlarınızı optimallaşdırmaq üçün bilik və təcrübəmizdən faydalanın.'
      )
    },
    [routes.valveMaintenance]: {
      title: t('Vana Bakım Hizmetlerimiz Hakkında', 'About Our Valve Maintenance Services', 'Vana Baxım Xidmətlərimiz Haqqında'),
      desc: t(
        'Profesyonel vana bakım, onarım ve test hizmetlerimiz hakkında detaylı bilgi almak için bizimle iletişime geçin.',
        'Contact us for detailed information about our professional valve maintenance, repair and testing services.',
        'Peşəkar vana baxım, təmir və test xidmətlərimiz haqqında ətraflı məlumat üçün bizimlə əlaqə saxlayın.'
      )
    },
    [routes.solutions]: {
      title: t('Size Nasıl Yardımcı Olabiliriz?', 'How Can We Help You?', 'Sizə Necə Kömək Edə Bilərik?'),
      desc: t(
        'Endüstriyel operasyon deneyimimizle akıllı vana yönetiminde yanınızdayız.',
        'We are by your side in smart valve management with our industrial operations expertise.',
        'Sənaye əməliyyat təcrübəmizlə ağıllı vana idarəçiliyində yanınızdayıq.'
      )
    },
    [routes.products]: {
      title: t('Doğru Ürünü Birlikte Seçelim', 'Let’s Choose the Right Product Together', 'Doğru Məhsulu Birlikdə Seçək'),
      desc: t(
        'Sahanıza en uygun vana kilitleme ve aktüatör çözümünü birlikte belirleyelim.',
        "Let's determine the most suitable valve interlock and actuator solution for your site.",
        'Sahəniz üçün ən uyğun vana kilidləmə və aktuator həllini birlikdə müəyyən edək.'
      )
    },
    [routes.services]: {
      title: t('Bakım ve Servis Çözümlerini Birlikte Planlayalım', 'Let’s Plan Your Service Strategy Together', 'Servis Strategiyanızı Birlikdə Planlayaq'),
      desc: t(
        'Saha ihtiyaçlarınıza uygun bakım, devreye alma ve teknik destek için ekibimizle iletişime geçin.',
        'Contact our team for maintenance, commissioning and technical support tailored to your field needs.',
        'Saha ehtiyaclarınıza uyğun baxım, işə salma və texniki dəstək üçün komandamızla əlaqə saxlayın.'
      )
    },
    [routes.applications]: {
      title: t('Uygulamanızı Bulamadınız mı?', "Can't Find Your Application?", 'Tətbiqinizi Tapmadınız?'),
      desc: t(
        'Uzman ekibimiz size özel çözümler sunmaya hazır. Vana operasyonlarınızı birlikte daha güvenli hale getirelim.',
        'Our team can review the scenario with you and shape a field-ready solution for the valve operations that matter most.',
        'Komandamız sizə xüsusi həllər təqdim etməyə hazırdır. Vana əməliyyatlarınızı birlikdə daha təhlükəsiz edək.'
      )
    },
    [routes.industries]: {
      title: t('Sektörünüz için nasıl yardımcı olabiliriz?', 'How can we support your industry?', 'Sənayeniz üçün necə kömək edə bilərik?'),
      desc: t(
        'Sektörünüze özel mühendislik yaklaşımımızla doğru ürün ve hizmet kombinasyonunu birlikte belirleyelim.',
        'We can match the right product and service mix to your industry, operating risk and field conditions.',
        'Mühəndislik yanaşmamızla sahənizə uyğun məhsul və xidmət kombinasiyasını birlikdə müəyyən edək.'
      )
    },
    [routes.docs]: {
      title: t('Aradığınız Dökümanı Bulamadınız mı?', "Can't Find the Document You're Looking For?", 'Axtardığınız Sənədi Tapmadınız?'),
      desc: t(
        'Özel teknik döküman talepleriniz için ekibimizle iletişime geçin.',
        'Contact us for your custom technical documentation requests.',
        'Xüsusi texniki sənəd tələbləriniz üçün komandamızla əlaqə saxlayın.'
      )
    },
    [routes.customerStories]: {
      title: t('Bir Sonraki Başarı Hikayemiz Siz Olun', 'Your Site Could Be the Next Success Story', 'Növbəti Uğur Hekayəmiz Siz Ola Bilərsiniz'),
      desc: t(
        'Vana operasyonlarınızı nasıl iyileştirebileceğimizi birlikte değerlendirelim.',
        'Let us review your field conditions and show where valve safety, speed and consistency can be improved.',
        'Vana əməliyyatlarınızı necə yaxşılaşdıra biləcəyimizi birlikdə dəyərləndirək.'
      )
    },
    [routes.energyTransition]: {
      title: t(
        'Enerji dönüşümü projeniz için doğru vana operasyon mimarisini birlikte kuralım.',
        'Let us build the right valve operation architecture for your energy transition project.',
        'Enerji keçidi layihəniz üçün düzgün vana əməliyyat memarlığını birlikdə quraq.'
      ),
      desc: t(
        'Hidrojenden karbon yakalamaya kadar tüm geçiş senaryolarında, sahaya özel güvenlik ve verimlilik planı tasarlayalım.',
        'From hydrogen to carbon capture, let us design a site-specific safety and efficiency plan for every transition scenario.',
        'Hidrogendən karbon tutmaya qədər bütün keçid ssenarilərində sahəyə uyğun təhlükəsizlik və səmərəlilik planı hazırlayaq.'
      )
    }
  };

  const siloLoadingPaths = [
    '/cozumler/silo-ve-kamyon-yukleme-hassasiyeti',
    '/solutions/silo-and-truck-loading-precision',
    '/heller/silo-ve-kamyon-yukleme-deqiqliyi'
  ];

  const siloLoadingCta = {
    title: t(
      'Silo ve Kamyon Yüklemesinde Hassasiyet',
      'Precision in Silo and Truck Loading',
      'Silo və Kamyon Yükləməsində Dəqiqlik'
    ),
    desc: t(
      'QR doğrulama ve interlock mantığı ile yanlış silo, yanlış hat ve yanlış ürün yüklemesini fiziksel olarak bloke edin.',
      'Physically block wrong silo, wrong line and wrong product loading with QR verification and interlock logic.',
      'QR təsdiqi və interlock məntiqi ilə yanlış silo, yanlış xətt və yanlış məhsul yüklənməsini fiziki olaraq bloklayın.'
    )
  };

  const matchedSiloCta = siloLoadingPaths.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  const currentCta = matchedSiloCta ? siloLoadingCta : Object.entries(ctaByRoute).find(([routePath]) =>
    routePath === '/'
      ? location.pathname === '/'
      : location.pathname === routePath || location.pathname.startsWith(`${routePath}/`)
  )?.[1] || {
    title: t('Demart ile iş yapmak çok kolay!', 'Working with Demart is easy!', 'Demart ilə işləmək çox asandır!'),
    desc: t(
      'Vana operasyonlarınızı optimize etmek için bilgi ve uzmanlığımızdan faydalanın.',
      'Leverage our know-how to optimize your valve operations.',
      'Vana əməliyyatlarınızı optimallaşdırmaq üçün bilik və təcrübəmizdən faydalanın.'
    )
  };
  const showStoriesCta = [routes.home, routes.applications, routes.energyTransition].some(
    (routePath) => location.pathname === routePath || location.pathname.startsWith(`${routePath}/`)
  );

  return (
    <footer className="bg-gray-900 text-white">
      <section className="bg-gradient-to-r from-[#0f1d37] to-[#132a4d] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <h2 className="text-2xl md:text-3xl leading-tight tracking-[-0.01em] font-bold mb-3 max-w-4xl mx-auto">{currentCta.title}</h2>
          <p className="text-white/85 text-[1rem] md:text-[1.05rem] leading-relaxed mb-8 max-w-3xl mx-auto">{currentCta.desc}</p>
          <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4">
            <Link
              to={routes.contact}
              className="interactive-lift inline-flex items-center px-7 py-3 bg-[#213A8F] text-white font-semibold rounded-lg hover:bg-[#1A2F75] transition-colors"
            >
              {t('Bize Ulaşın', 'Contact Us', 'Bizə Ulaşın')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <span className="hidden md:inline text-white/60">{t('veya', 'or', 'və ya')}</span>
            <a
              href="tel:+902164011050"
              className="interactive-lift inline-flex items-center px-6 py-3 bg-transparent border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" />
              {t('Bizi hemen arayın:', 'Call us now:', 'İndi zəng edin:')} {primaryPhone}
            </a>
            <span className="hidden md:inline text-white/60 whitespace-nowrap">
              {t('veya', 'or', 'və ya')}
            </span>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="interactive-lift inline-flex items-center px-6 py-3 bg-transparent border border-emerald-300/50 text-emerald-200 rounded-lg hover:bg-emerald-600/20 transition-colors"
            >
              <WhatsAppIcon className="w-4 h-4 mr-2" />
              {t('WhatsApp’tan iletişime geçin', 'Reach us on WhatsApp', 'WhatsApp ilə əlaqə saxlayın')}
            </a>
          </div>
          {showStoriesCta && (
            <div className="mt-6 flex justify-center">
              <Link
                to={routes.customerStories}
                className="group inline-flex items-center gap-2 px-5 py-2.5 min-h-[44px] rounded-lg border border-white/25 bg-white/5 text-white/90 font-semibold text-sm hover:bg-white/10 hover:text-white transition-all"
              >
                {t('Başarı Hikayelerini İnceleyin', 'Explore Success Stories', 'Uğur Hekayələrinə Baxın')}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          )}
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <h3 className="text-lg font-bold mb-4">{footerText.aboutTitle}</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              {footerText.aboutDesc}
            </p>
            <div className="flex space-x-4">
              <a href="https://www.linkedin.com/company/10896758" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-11 h-11 text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">{t('LinkedIn sayfamız', 'Our LinkedIn page', 'LinkedIn səhifəmiz')}</span>
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://www.youtube.com/channel/UCb6NwXGwNOg_D13XP7UJEaQ" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-11 h-11 text-gray-400 hover:text-red-500 transition-colors">
                <span className="sr-only">{t('YouTube kanalımız', 'Our YouTube channel', 'YouTube kanalımız')}</span>
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4">{footerText.quickLinks}</h3>
            <div className="grid grid-cols-2 gap-x-6">
              <ul className="space-y-1">
                {quickLinksLeftColumn.map((link, idx) => (
                  <li key={`quick-left-${idx}`}>
                    <Link to={link.href} className="text-gray-400 hover:text-[#213A8F] text-sm transition-colors">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <ul className="space-y-1">
                {quickLinksRightColumn.map((link, idx) => (
                  <li key={`quick-right-${idx}`}>
                    <Link to={link.href} className="text-gray-400 hover:text-[#213A8F] text-sm transition-colors">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4">{footerText.productsTitle}</h3>
            <ul className="space-y-1">
              {productLinks.map((link, idx) => (
                <li key={idx}>
                  <Link to={link.href} className="text-gray-400 hover:text-[#213A8F] text-sm transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4">{footerText.contactTitle}</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-[#213A8F] mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  {company?.fullAddress || company?.address?.street}
                </span>
              </div>

              <div className="flex items-center">
                <Phone className="w-5 h-5 text-[#213A8F] mr-3 flex-shrink-0" />
                <a href="tel:+902164011050" className="text-gray-400 hover:text-white text-sm transition-colors">
                  {primaryPhone}
                </a>
              </div>

              <div className="flex items-center">
                <WhatsAppIcon className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0" />
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  {secondaryPhone}
                </a>
              </div>

              {company?.email && (
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-[#213A8F] mr-3 flex-shrink-0" />
                  <a href={`mailto:${company.email}`} className="text-gray-400 hover:text-white text-sm transition-colors">
                    {company.email}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-10 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">{footerText.copyright}</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to={routes.privacy || '/privacy'} className="text-gray-400 hover:text-white text-sm transition-colors">
              {footerText.privacyPolicy}
            </Link>
            <Link to={routes.cookies || '/cookies'} className="text-gray-400 hover:text-white text-sm transition-colors">
              {footerText.cookiePolicy}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
